import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `
Política de Privacidad — Vitalízate Ahora

Última actualización: Abril 2026

En Vitalízate Ahora, respetamos y protegemos tu privacidad. Esta política describe cómo recopilamos, usamos y protegemos tu información personal.

Información que recopilamos:
• Nombre y dirección de correo electrónico al realizar una compra.
• Información de pago procesada de forma segura a través de proveedores externos.
• Datos de navegación anónimos para mejorar la experiencia del usuario.

Uso de la información:
• Procesar y entregar tu pedido.
• Enviar confirmaciones de compra y acceso a productos digitales.
• Mejorar nuestros productos y servicios.

No vendemos ni compartimos tu información personal con terceros para fines de marketing.

Seguridad:
Utilizamos encriptación SSL y medidas de seguridad estándar de la industria para proteger tus datos.

Tus derechos:
Puedes solicitar la eliminación de tus datos personales en cualquier momento contactándonos a través de nuestro sitio web.
`.trim();
const termsContent = `
Términos y Condiciones — Vitalízate Ahora

Última actualización: Abril 2026

Al acceder y utilizar este sitio web y adquirir nuestros productos, aceptas los siguientes términos:

Productos:
• Los suplementos dietéticos no están destinados a diagnosticar, tratar, curar o prevenir ninguna enfermedad.
• Los resultados pueden variar de persona a persona.
• Consulta a tu médico antes de iniciar cualquier suplemento, especialmente si estás embarazada, amamantando o tomando medicamentos.

Productos digitales:
• Los eBooks y guías digitales se entregan en formato PDF tras la confirmación del pago.
• Por la naturaleza digital del producto, no se aceptan devoluciones una vez descargado.

Propiedad intelectual:
• Todo el contenido de este sitio, incluyendo textos, imágenes y guías, es propiedad de Vitalízate Ahora.
• Queda prohibida su reproducción o distribución sin autorización.

Limitación de responsabilidad:
• Vitalízate Ahora no se hace responsable por el uso inadecuado de los productos o la información proporcionada.
• La información en este sitio es de carácter educativo y no sustituye el consejo médico profesional.
`.trim();
function Modal({
  type,
  onClose
}: {
  type: ModalType;
  onClose: () => void;
}) {
  if (!type) return null;
  const title = type === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones';
  const content = type === 'privacy' ? privacyContent : termsContent;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-47">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-48" />

      {/* Modal */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.3
    }} onClick={e => e.stopPropagation()} className="relative w-full max-w-2xl max-h-[80vh] rounded-2xl overflow-hidden" style={{
      background: '#0D1330',
      border: '1px solid rgba(78, 205, 196, 0.15)'
    }} data-id="element-49">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10" data-id="element-50">
          <h3 className="font-display text-xl font-bold text-white" data-id="element-51">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-colors" aria-label="Cerrar" data-id="element-52">
            <XIcon className="w-5 h-5" data-id="element-53" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-54">
          <pre className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap font-sans" data-id="element-55">
            {content}
          </pre>
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [modal, setModal] = useState<ModalType>(null);
  return <>
      <footer className="py-8 px-4 border-t border-white/5" data-id="element-56">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-57">
          <p className="text-gray-500 text-sm" data-id="element-58">
            © {new Date().getFullYear()} Vitalízate Ahora. Todos los derechos
            reservados.
          </p>
          <div className="flex items-center gap-6" data-id="element-59">
            <button onClick={() => setModal('privacy')} className="text-gray-500 text-sm hover:text-[#4ECDC4] transition-colors" data-id="element-60">
              Política de Privacidad
            </button>
            <button onClick={() => setModal('terms')} className="text-gray-500 text-sm hover:text-[#4ECDC4] transition-colors" data-id="element-61">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-62">
        {modal && <Modal type={modal} onClose={() => setModal(null)} data-id="element-63" />}
      </AnimatePresence>
    </>;
}